
import Vue, { PropType } from 'vue';
import {
  CvGrid,
  CvButton,
  CvRow,
  CvColumn,
  CvButtonSet,
  CvLoading,
} from '@carbon/vue';
import GeneratePdf16 from '@carbon/icons-vue/es/generate-pdf/16';
import {
  getArtworkFiles,
  getSmallDetailsFiles,
  getVariantFiles,
  getTextFiles,
} from '@/utils/personalizationHelper';
import { mapActions, mapState } from 'pinia';
import { useStore as useProductStore } from '@/stores/product';
import { textShapes, useStore as useShapeStore } from '@/stores/shape';
import { useStore as useNotificationStore } from '@/stores/notification';
import { useStore as useShapeBoxSectionStore } from '@/stores/shapeBoxSection';
import { getProofFormFromState } from '@/utils';
import customerService from '@/services/CustomersService';
import Close32 from '@carbon/icons-vue/es/close/16';
import { UpdatePersonalizationCommand } from '@/web-components/import-wizard/typings/common/createPersonalization';
import {
  CreatePersonalizationForm,
  Personalization,
} from '@midocean/webapps-shared-domain';
import { useStore as useLangStore } from '@/stores/lang';
import customersAPI from '@/services/CustomersService';
import { DoSmallDetailsPart } from '@/constants/imageProcessingEngine';
import { revalidateSmallDetails } from '../SmallDetails';
import { PipelineNotificationType } from '../Notifiers/typings/PipelineNotificationType';

export default Vue.extend({
  name: 'PreviewPdf',
  components: {
    CvButton,
    CvRow,
    CvColumn,
    CvLoading,
    GeneratePdf16,
    CvButtonSet,
    CvGrid,
    Close32,
  },
  data() {
    return {
      isCreatingPdf: false,
      pdf: '',
      isModalVisible: false,
      configDefaults: {
        sidebar: {
          viewThumbnail: true,
          viewOutline: false,
          viewAttachments: false,
        },
        secondaryToolbar: false,
        toolbar: {
          toolbarViewerRight: {
            viewBookmark: false,
            openFile: false,
          },
          toolbarViewerLeft: {
            findbar: false,
          },
        },
      },
      pageScale: 'page-fit',
      logoImgSrc: require('../../assets/pdfLogo.png'),
      pdfImg: '',
      isOrderingDisabled: false,
      personalization: {} as Personalization,
    };
  },
  props: {
    pdpChoices: {
      type: Object as PropType<any>,
      required: false,
      default: {} as any,
    },
    personalizationId: {
      type: String,
      required: false,
      default() {
        return '';
      },
    },
    sapCustomerId: {
      type: String,
      required: true,
    },
    customerCompanyLogoUrl: {
      type: String,
      required: false,
      default: '',
    },
    isDirectApproval: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    ...mapState(useShapeStore, ['shapes']),
    ...mapState(useProductStore, ['data', 'unavailablePrintPositions']),
    ...mapState(useNotificationStore, ['notificationsForBell']),
    ...mapState(useShapeBoxSectionStore, ['getIsValidInput']),
    ...mapState(useLangStore, ['languageInUse']),

    isEditMode(): boolean {
      return this.personalizationId !== '';
    },
  },
  mounted() {
    this.showModal();
  },
  methods: {
    ...mapActions(useProductStore, ['setSelectedVariant']),
    ...mapActions(useNotificationStore, ['updateSmallDetailsNotifications']),

    async trySavePersonalization() {
      this.isOrderingDisabled = true;
      const { sku, name } = this.data;

      const files = await getArtworkFiles(this.shapes);
      const variantFiles = getVariantFiles(this.shapes);
      const smallDetailsFiles = DoSmallDetailsPart
        ? getSmallDetailsFiles(this.shapes)
        : [];
      const textFiles = getTextFiles(this.shapes);

      try {
        if (!this.isEditMode) {
          const { productVariants: pdpChoicesProductVariants } =
            this.pdpChoices;

          const form: CreatePersonalizationForm = {
            externalCustomerId: this.sapCustomerId,
            productSKU: sku,
            productName: name,
            isConcept: false,
            isDirectApproval: this.isDirectApproval,
            customerCompanyLogoUrl: this.customerCompanyLogoUrl,
            contactLanguageCode: this.languageInUse,
            creator: '',
            proofType: 'AdvancedPersonalization',
            productVariants: pdpChoicesProductVariants,
            newProofForm: getProofFormFromState(false),
          } as unknown as CreatePersonalizationForm;

          const {
            data: { id: personalizationId },
          } = await customerService.submitPersonalization(
            files,
            variantFiles,
            smallDetailsFiles,
            textFiles,
            form
          );

          this.sendEvent({ personalizationId });
          this.$emit('action:disable-pintura');
        }

        if (this.isEditMode) {
          const command: UpdatePersonalizationCommand = {
            name: this.personalization.proofs[0].name ?? '',
            remarkCodes: this.notificationsForBell
              .filter(
                (notification: PipelineNotificationType) =>
                  notification.validatorName === 'commercialRemark'
              )
              .map((notification) => notification.message),
            ...getProofFormFromState(false),
          };
          await customerService.updatePersonalization(
            files,
            variantFiles,
            smallDetailsFiles,
            textFiles,
            command,
            this.personalization.id,
            this.personalization.proofs[0].id
          );

          //Check if Order this item btn is clicked, if yes - complete personalization
          await customerService.completePersonalization(
            this.personalization.id
          );

          this.sendEvent({ personalizationId: this.personalization.id });
          this.$emit('action:disable-pintura');
        }
      } catch (e: unknown) {
        console.log((e as Error).message);
        this.isOrderingDisabled = false;
      }
    },

    onToShoppingCard() {
      const triggerSmallDetailsCheck =
        this.shapes.filter(
          (x) =>
            x.smallDetailsResult &&
            (x.smallDetailsResult.status === 'Average' ||
              x.smallDetailsResult.status === 'Bad' ||
              !x.smallDetailsResult.isCheckValid)
        ).length > 0;

      if (DoSmallDetailsPart && triggerSmallDetailsCheck) {
        this.$emit('action:trigger-small-details');
        this.$emit('action:update-is-concept', false); // only option is to create order
      } else {
        this.trySavePersonalization();
      }
    },

    sendEvent(payload: { personalizationId: string }) {
      if (!this.$root.$refs.inner) return;
      (this.$root.$refs.inner as Vue).$emit('wizard:ordered', payload);
    },

    async showModal() {
      if (this.isEditMode) {
        const { data } = await customersAPI.getPersonalization(
          this.personalizationId
        );
        this.personalization = data;
      }
      this.isCreatingPdf = true;

      const isRevalidationNeeded =
        this.shapes.filter(
          (x) =>
            x.smallDetailsResult && x.smallDetailsResult.isCheckValid === false
        ).length > 0;

      if (DoSmallDetailsPart && isRevalidationNeeded) {
        await revalidateSmallDetails();

        this.updateSmallDetailsNotifications();
      }

      const smallDetailsFiles = DoSmallDetailsPart
        ? getSmallDetailsFiles(this.shapes)
        : [];

      const files = await getArtworkFiles(this.shapes);
      const variantFiles = getVariantFiles(this.shapes);
      const textFiles = getTextFiles(this.shapes);
      const { productVariants: pdpChoicesProductVariants } = this.pdpChoices;
      const { sku, name } = this.data;
      const { newProductPrintPositionForms } = getProofFormFromState(true);
      const remarkCodes = this.notificationsForBell
        .filter(
          (notification: PipelineNotificationType) =>
            notification.validatorName === 'commercialRemark'
        )
        .map((notification) => notification.message);

      const { data } = await customerService.generateProofPreview(
        files,
        variantFiles,
        smallDetailsFiles,
        textFiles,
        {
          externalCustomerId: this.sapCustomerId,
          productSKU: sku,
          productName: name,
          customerCompanyLogoUrl: this.customerCompanyLogoUrl,
          contactLanguageCode: this.languageInUse,
          proofType: 'AdvancedPersonalization',
          productVariants: pdpChoicesProductVariants,
          productPrintPositionForms: newProductPrintPositionForms,
          remarkCodes: remarkCodes,
        }
      );

      this.isCreatingPdf = false;
      this.pdf = data.pdfUrl;
      this.pdfImg = data.pdfPreviewUrl;
      this.$modal.show('pdf-preview-modal');
    },
    onCloseModal() {
      this.pdf = '';
      this.pdfImg = '';
      this.$emit('close:preview-pdf');
    },
    async onPreviewPdf(downloadPdfPreviewUrl: string) {
      // in case of usage proof approval component from Backend portal do not use
      // vue pdf app for pdf preview, open pdf in a new tab in browser pdf viewer

      try {
        window.open(downloadPdfPreviewUrl, '_blank');
      } catch (e) {
        console.log(e);
      }
    },
  },
});
